<template>
  <v-container>
    <v-form
      ref="form"
      v-model="isValid"
    >
      <validation-observer ref="observer">
        <v-row>
          <v-col cols="12">
            <validated-text-field
              v-model="c.name"
              rules="required|min:2|max:255"
              :label="$t('label.business_name')"
            />
          </v-col>
          <v-col cols="12">
            <validated-text-field
              v-model="c.email"
              rules="required|validEmail|max:255"
              :label="$t('label.email')"
            />
          </v-col>
          <v-col cols="12">
            <validated-text-field
              v-if="taxInfo.isVat"
              v-model="c.vatNumber"
              :label="$t('label.vat_number')"
              :rules="taxInfo.rules"
            />
            <validated-text-field
              v-else
              v-model="c.taxNumber"
              :label="$t('label.tax_number')"
              :rules="taxInfo.rules"
            />
          </v-col>
          <v-col cols="12">
            <validated-text-field
              v-model="c.addressLine1"
              :label="$t('label.street')"
              rules="required|min:2|max:255"
            />
          </v-col>
          <v-col cols="8">
            <validated-text-field
              v-model="c.city"
              rules="required|min:2|max:255"
              :label="$t('label.city')"
            />
          </v-col>
          <v-col cols="4">
            <validated-text-field
              v-model="c.postalCode"
              :label="$t('label.zipcode')"
              rules="required|min:2|max:50"
            />
          </v-col>
          <v-col cols="8">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                v-model="c.country"
                class="pt-0"
                :label="$t('label.country')"
                :placeholder="$t('label.country')"
                :error-messages="errors"
                :items="countries"
              />
            </validation-provider>
          </v-col>
          <v-col
            v-if="stateInfo.visible"
            cols="4"
          >
            <validated-text-field
              v-model="c.state"
              :label="$t('label.state')"
              :rules="stateInfo.rules"
            />
          </v-col>
        </v-row>
      </validation-observer>
    </v-form>
  </v-container>
</template>

<script setup lang="ts">

import Vue, {
  PropType, ref, Ref, computed, onMounted, watch, inject,
} from 'vue';
import {
  Customer, Invoice, InvoiceStatus, Price, Country as BillingCountry, BillingConvert,
} from '@/api/api-billing';
import Country from '@/model/Country';
import store from '@/store/store';
import {
  isEUVATCountry, isOriginCountry, toDTOCustomer, validateVatNumberFormat,
} from '@/services/billing-utils';
import httpClient from '@/api/http-client';
import i18n from '@/plugins/i18n';
import { performSaveAction } from '@/services/vue-utils';
import { ValidationObserver, extend } from 'vee-validate';
import { PaymentWizardMode, PaymentWizardStep, PaymentWizardStepKey } from '@/services/payment-wizard-utils';

extend('vat', {
  validate(value, params) { return validateVatNumberFormat(value); },
  message: (field, args) => i18n.tc('error.field_incorrect_vat_format'),
});

const props = defineProps({
  mode: { type: String as PropType<PaymentWizardMode>, default: undefined },
  embedded: { type: Boolean as PropType<boolean>, required: false, default: false },
});

const emit = defineEmits(['submit', 'is-changed']);

const observer = ref<InstanceType<typeof ValidationObserver>>();

const c = ref({}) as Ref<Customer>;
const oldc = ref({}) as Ref<Customer>;
const countries = ref([]) as Ref<Country[]>;

const isValid = ref(false) as Ref<boolean>;

const isNew = computed(() => !store.getters.isBillingCustomer);

const isChanged = computed(
  () => isNew.value || BillingConvert.customerToJson(c.value) !== BillingConvert.customerToJson(oldc.value),
);

const stateInfo = computed(() => ({
  visible: true,
  rules: 'required|min:2|max:50',
}));

const taxInfo = computed(() => {
  // nl -> vat number is not required
  if (isOriginCountry(c.value.country)) {
    return {
      isVat: true,
      rules: 'min:2|max:20|vat',
    };
  }

  // EU VAT countries + GB -> vat number is required
  if (isEUVATCountry(c.value.country)) {
    return {
      isVat: true,
      rules: 'required|min:2|max:20|vat',
    };
  }

  // non-EU countries nor GB -> tax number is not required
  return {
    isVat: false,
    rules: 'min:2|max:20',
  };
});

const loadCustomer = () => {
  oldc.value = toDTOCustomer(store.getters.billingCustomer);
  c.value = { ...oldc.value };
  countries.value = store.getters.sortedCountries;

  if (!isNew.value) return;

  const a = store.state.settings.account;
  const country = store.getters.billingGuessedCountry as BillingCountry | null;

  if (country && country.countryCode !== a.countryCode) {
    // guessed country and account country do not match
    c.value = {
      name: a.accountName,
      email: a.email,
      country: country.countryCode,
    };
  } else {
    // no guessed country or guessed country matches account country
    c.value = {
      name: a.accountName,
      email: a.email,
      addressLine1: a.streetAddress,
      city: a.city,
      postalCode: a.postalCode,
      country: a.countryCode,
      state: a.countryState,
    };
  }
};

const autofillCountry = async () => {
  try {
    const countryCode = await httpClient.getCountryCode();
    console.log('autofillCountry:', countryCode, c.value.country);
    if (!countryCode || c.value.country) return; // no country code detected or no need to update it any more
    Vue.set(c.value, 'country', countryCode); // autofill country code
  } catch (e) {
    console.log('Autofill country code error: ', e);
  }
};

const reset = () => {
  loadCustomer();
  if (c.value && !c.value.country) autofillCountry();
};

const submit = async () => {
  if (!isChanged.value) {
    emit('submit', c.value);
    return true;
  }

  const customer = { ...c.value };
  if (!taxInfo.value.isVat) {
    customer.vatNumber = undefined;
  } else {
    customer.taxNumber = undefined;
  }
  customer.locale = i18n.locale ?? '';

  const action = isNew.value ? 'billingCreateCustomer' : 'billingUpdateCustomer';
  const ok = await performSaveAction(
    observer.value,
    async () => store.dispatch(action, { customer }),
    { hideSuccess: props.embedded },
  );

  if (!ok) return false;

  emit('submit', c.value);

  return true;
};

onMounted(() => {
  reset();
});

// wizard embeddable
const addStep = inject('addStep') as ((s: PaymentWizardStep) => void) | undefined;
const stepChanged = inject('stepChanged') as ((s: PaymentWizardStep) => void) | undefined;

const paymentWizardStep = computed(() => {
  const complete = props.mode === PaymentWizardMode.BillingDetails ? isChanged.value && isValid.value : true;
  const submitText = props.mode === PaymentWizardMode.BillingDetails ? i18n.tc('button.save') : undefined;

  return {
    key: PaymentWizardStepKey.BillingDetails,
    label: i18n.tc('label.billing_details'),
    complete,
    submitAction: submit,
    submitText,
  } as PaymentWizardStep;
});

watch(
  () => paymentWizardStep.value,
  (value) => { if (stepChanged) stepChanged(value); },
);

if (addStep) addStep(paymentWizardStep.value);

</script>
